import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LucideArrowRight } from "lucide-react";

import Layout from "src/components/program/layout/Layout";
import SEO from "src/components/common/SEO";
import HeroBanner from "src/components/program/HeroBanner";
import Button from "src/components/app/Button";
import ProgramOfferings from "src/components/program/ProgramOfferings";
import ImageContainer from "src/components/program/ImageContainer";
import ProgramFAQs from "src/components/program/ProgramFAQs";
import CopyRights from "src/components/program/CopyRights";
import RequestCallback from "src/components/program/RequestCallback";

import { getUrlParams } from "src/helpers/common";
import supportPrograms from "src/data/app/programs";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

const ProgramScreen: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const config =
    supportPrograms[params.support_program_key as keyof typeof supportPrograms];

  const innerRef = useRef<HTMLDivElement>(null);
  const heroBannerRef = useRef<HTMLDivElement>(null);
  const [addbottom, setBottom] = useState(false);
  const [urlParams] = useState(getUrlParams());

  useEffect(() => {
    analytics.pageView(SCREEN_NAMES.PROGRAM_DETAILS_SCREEN);
    const div = innerRef.current;
    div?.addEventListener("scroll", handleOnScroll);
    return () => div?.removeEventListener("scroll", handleOnScroll);
  }, []);

  const handleOnScroll = (e: any) => {
    const heroHeight = heroBannerRef.current?.offsetHeight ?? 0;
    const threshold = heroHeight * 0.9;
    setBottom(e.target.scrollTop >= threshold);
  };

  const handleJoinProgramClick = () => {
    analytics.trackEvent(EVENT_NAMES.JOIN_PROGRAM_CLICKED, {
      program_name: config?.psp_name,
    });
    navigate(`/programs/${params.support_program_key}/onboarding`);
  };

  const footerContent =
    (urlParams as any)?.mode !== "view_only" ? (
      <div
        className="container w-full mx-auto text-center bg-white border-t border-gray-200"
        style={{ padding: "15px 0" }}
      >
        <div className="px-4">
          <Button
            onClick={handleJoinProgramClick}
            variant="contained"
            fullWidth
          >
            <div className="flex flex-row items-center justify-center gap-2">
              <span>Join the Program</span>
              <LucideArrowRight />
            </div>
          </Button>
        </div>
      </div>
    ) : undefined;

  const showFooter = (urlParams as any)?.mode !== "view_only" && addbottom;

  return (
    <Layout
      footer={showFooter ? footerContent : undefined}
      stickyFooter={showFooter}
      enableTheme={true}
      supportProgramKey={params.support_program_key}
    >
      <SEO
        title={`${config?.psp_name} - GoodFlip Care`}
        description={`${config?.psp_name} - Program Offerings, Benefits, FAQs and more.`}
        keywords={`GoodFlip Care, ${config?.psp_name}, Personalised Support Programs, Health Support, Doctor Guidance, Monitoring`}
      />
      <div className="bg-gray-50 relative">
        <div
          className="container h-[100vh] overflow-y-auto bg-white flex flex-col gap-10"
          style={{
            padding: 0,
            overflow: "auto",
            paddingBottom:
              (urlParams as any)?.mode === "view_only" ? "10px" : "90px",
          }}
          ref={innerRef}
        >
          <div ref={heroBannerRef}>
            <HeroBanner
              image={config?.website?.hero_image}
              onButtonClick={handleJoinProgramClick}
              mode={(urlParams as any)?.mode}
            />
          </div>

          <ProgramOfferings content={config?.website?.benefits} />

          {config?.website?.colored_card && (
            <ImageContainer imageUrl={config?.website?.colored_card?.image} />
          )}

          {config?.website?.start_journey_steps?.below_care_image && (
            <ImageContainer
              imageUrl={config?.website?.start_journey_steps?.below_care_image}
            />
          )}

          <ProgramFAQs {...config?.website?.faqs} />

          {(urlParams as any)?.mode !== "view_only" && (
            <RequestCallback programName={config?.psp_name} />
          )}

          <div className="flex flex-col gap-3">
            {config?.website?.disclaimer && (
              <div className="px-4">
                <div className="text-center">
                  <div className="text-xs sm:text-sm font-medium text-gray-600">
                    {config?.website?.disclaimer?.title}
                  </div>

                  <div className="mt-2 mb-3 border-t border-gray-300 w-full" />

                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed text-justify">
                    {config?.website?.disclaimer?.description}
                  </p>
                </div>
              </div>
            )}
            <CopyRights />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProgramScreen;
